import { Box, Button, Flex } from "@jewlr/storybook/core"
import { FocusScope } from "@react-aria/focus"
import { useId } from "@react-aria/utils"
import PropTypes from "prop-types"
import { useState, useRef } from "react"
import styled from "styled-components"

import LandingSlider from "areas/landing/elements/landing-slider"
import Textarea from "components/landing/textarea"
import { useNavigation } from "helpers/use-navigation"
import useWindowDimensions from "helpers/window-dimensions"
import { hideScrollbar } from "styles/utils"

const FilterContainer = styled(Flex).attrs((props) => ({
  mb: "40px",
  px: { _: "40px", desktop: 0 },
  ...props,
}))`
  -webkit-overflow-scrolling: touch;
  overflow-x: scroll;
  white-space: nowrap;
  ${hideScrollbar}
  ${(props) => props.theme.mediaQueries.desktop`
    white-space: normal;
    justify-content: center;
    overflow-x: auto;
    overflow-y: auto;
  `}
`

const StyledButton = styled(Button).attrs((props) => ({
  variant: "text",
  ...props,
}))`
  padding: 8px 16px;
  ${(props) =>
    props.selected &&
    `
    color: ${props.selectedColor};
    background-color: ${props.selectedBg};
    font-weight: 700;
    text-decoration: underline;
  `}
  &:focus {
    outline-offset: -2px;
  }
`

const TabButton = ({ onClick, selected, item, ...props }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <StyledButton
      aria-selected={selected}
      onClick={onClick}
      role="tab"
      selected={selected}
      tabIndex={selected ? "0" : "-1"}
      {...props}
      {...keyboardProps}
    >
      {item.name}
    </StyledButton>
  )
}

TabButton.propTypes = {
  item: PropTypes.object,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
}

const CarouselFiltered = ({ element }) => {
  const [selectedCategory, setSelectedCategory] = useState(
    element.super_filters[0]
  )
  const sliderRef = useRef()
  const { isMobile } = useWindowDimensions()
  const filterable = element.super_filters.length > 1
  const carouselId = useId()

  const resetSlider = () => {
    if (sliderRef?.current) {
      if (isMobile) {
        sliderRef.current.scrollLeft = 0
      } else {
        sliderRef.current.slickGoTo(0, true)
      }
    }
  }

  return (
    <Box
      mb={9}
      pb={{ _: 4, widescreen: "60px" }}
      pt={{ _: 4, widescreen: "60px" }}
      {...element.container_style}
    >
      {element.textarea && (
        <Box
          mb={{ _: 4, tablet: 5 }}
          px={{ _: 2, tablet: 4, widescreen: "60px" }}
          textAlign="center"
          {...element.style?.textarea_container_style}
        >
          <Textarea
            element={element.textarea}
            linkClickData={element?.anchor_name || element?.id}
            style={element.textarea.style}
          />
        </Box>
      )}
      {filterable && (
        <FilterContainer
          aria-label={element.name}
          role="tablist"
          {...element.style?.filter_container_style}
        >
          <FocusScope>
            {element.super_filters.map((item, i) => {
              return (
                <TabButton
                  aria-controls={`${carouselId}-${item.key}-tabpanel`}
                  id={`${carouselId}-${item.key}-tab`}
                  item={item}
                  key={`${carouselId}_tablist_${i}`}
                  onClick={() => {
                    setSelectedCategory(item)
                    resetSlider()
                  }}
                  selected={selectedCategory.key === item.key}
                  {...element.style?.filter_item_style}
                />
              )
            })}
          </FocusScope>
        </FilterContainer>
      )}
      <LandingSlider
        element={{
          ...element,
          arrow_style: {
            arrowIcon: "arrow-dark-transparent.svg",
            height: "60px",
            top: { desktop: "42%", tablet: "32%" },
            width: "100%",
            ...element.arrow_style,
          },
          item_width: {
            _: "70vw",
            tablet: 4,
            ...element.style?.item_width,
          },
          items: element.data[selectedCategory.key],
          slider_settings: {
            ...element.sliderSettings,
            ...element.slider_settings,
            frameAriaLabel: selectedCategory?.name,
            tabbed: !filterable,
          },
          slider_type: "native",
          style: {
            fontSize: "8px",
            mx: { tablet: -1 },
            ...element.style,
          },
          tabPanelProps: filterable && {
            "aria-labelledby": `${carouselId}-${selectedCategory.key}-tab`,
            id: `${carouselId}-${selectedCategory.key}-tabpanel`,
            role: "tabpanel",
          },
        }}
        lazyload={element.lazyload}
        sliderRef={sliderRef}
      />
    </Box>
  )
}

CarouselFiltered.propTypes = {
  element: PropTypes.object,
}

export default CarouselFiltered
